import type { StreamFieldCondition, StreamSearchFilters } from './streamsApi'

/** Minimal shape `matchesStreamSearchFilters` needs from a stream-like object. */
export interface StreamSearchFiltersMatchable {
	name: string
	fields: Array<{ name: string; type: string }>
}

function matchesFieldCondition(value: string, condition: StreamFieldCondition): boolean {
	if (condition.eq !== undefined && value !== condition.eq) {
		return false
	}
	if (condition.ne !== undefined && value === condition.ne) {
		return false
	}
	if (condition.in !== undefined && !condition.in.includes(value)) {
		return false
	}
	if (condition.regex !== undefined && !value.toLowerCase().includes(condition.regex.toLowerCase())) {
		return false
	}
	return true
}

/**
 * Evaluate a compiled `StreamSearchFilters` tree against a stream-like object, client-side — the same
 * evaluation the real backend does for `POST /v3/streams/search`. Shared by the demo in-memory backend
 * (which has no real backend to ask) and the `device_state` pinned-row matcher (which isn't a real
 * stream, so it never appears in a `/v3/streams/search` page and has to be matched locally).
 */
export function matchesStreamSearchFilters(
	stream: StreamSearchFiltersMatchable,
	filters: StreamSearchFilters,
): boolean {
	if ('and' in filters) {
		return filters.and.every(f => matchesStreamSearchFilters(stream, f))
	}
	if ('or' in filters) {
		return filters.or.some(f => matchesStreamSearchFilters(stream, f))
	}
	if ('not' in filters) {
		return !matchesStreamSearchFilters(stream, filters.not)
	}
	if ('stream_name' in filters) {
		return matchesFieldCondition(stream.name, filters.stream_name)
	}
	if ('field_name' in filters) {
		return stream.fields.some(field => matchesFieldCondition(field.name, filters.field_name))
	}
	// 'field_type'
	return stream.fields.some(field => matchesFieldCondition(field.type, filters.field_type))
}
